import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    CircularProgress,
    Dialog,
    Grid,
    IconButton,
    Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import EventIcon from "@material-ui/icons/Event";
import CloseIcon from "@material-ui/icons/Close";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import StudentCards from "./UserProfileCard";
import StudentService from "./Locality/Service/studentService";

const useStyles = makeStyles((theme) => ({
    pageContainer: {
        maxWidth: 1080,
        margin: "0 auto",
        padding: theme.spacing(3),
        backgroundColor: "#f8fafc",
        minHeight: "100%",
    },
    studentSelectorCard: {
        borderRadius: 18,
        padding: theme.spacing(1),
        marginBottom: theme.spacing(3),
        background: "#ffffff",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
    },
    headerCard: {
        borderRadius: 16,
        backgroundColor: "#ffffff",
        border: "1px solid #e6eaf0",
        boxShadow: "0 2px 12px rgba(16, 42, 67, 0.06)",
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(3),
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1.5),
    },
    headerIcon: {
        width: 48,
        height: 48,
        borderRadius: 12,
        backgroundColor: "#eef4ff",
        color: "#1769d6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    headerTitle: {
        fontWeight: 700,
        fontSize: "1.45rem",
        color: "#0f2d4d",
        lineHeight: 1.25,
    },
    headerSubtitle: {
        color: "#6b7280",
        fontSize: "0.875rem",
        marginTop: 4,
    },
    albumCard: {
        borderRadius: 16,
        backgroundColor: "#ffffff",
        border: "1px solid #e6eaf0",
        boxShadow: "0 2px 12px rgba(16, 42, 67, 0.06)",
        overflow: "hidden",
        marginBottom: theme.spacing(2.5),
    },
    albumHeader: {
        padding: theme.spacing(2, 2.5),
        borderBottom: "1px solid #eef1f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    albumTitle: {
        fontWeight: 700,
        color: "#0f2d4d",
        fontSize: "1.05rem",
    },
    albumMeta: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        color: "#64748b",
        fontSize: "0.85rem",
    },
    photoCount: {
        backgroundColor: "#eef4ff",
        color: "#1769d6",
        fontWeight: 700,
        fontSize: "0.75rem",
        padding: "4px 10px",
        borderRadius: 999,
    },
    albumGrid: {
        padding: theme.spacing(2),
    },
    photoTile: {
        position: "relative",
        width: "100%",
        paddingTop: "100%",
        borderRadius: 12,
        overflow: "hidden",
        cursor: "pointer",
        backgroundColor: "#f1f5f9",
        border: "1px solid #e2e8f0",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
            transform: "scale(1.02)",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
        },
    },
    photoImage: {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
    emptyState: {
        padding: theme.spacing(6, 2),
        textAlign: "center",
        color: "#64748b",
        backgroundColor: "#ffffff",
        borderRadius: 16,
        border: "1px solid #e6eaf0",
    },
    lightboxPaper: {
        backgroundColor: "rgba(15, 23, 42, 0.95)",
        boxShadow: "none",
        maxWidth: "96vw",
        maxHeight: "96vh",
    },
    lightboxContent: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "70vh",
        padding: theme.spacing(2),
    },
    lightboxImage: {
        maxWidth: "100%",
        maxHeight: "78vh",
        objectFit: "contain",
        borderRadius: 8,
    },
    lightboxClose: {
        position: "absolute",
        top: 8,
        right: 8,
        color: "#ffffff",
        backgroundColor: "rgba(255,255,255,0.12)",
        "&:hover": {
            backgroundColor: "rgba(255,255,255,0.2)",
        },
    },
    lightboxNav: {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        color: "#ffffff",
        backgroundColor: "rgba(255,255,255,0.12)",
        "&:hover": {
            backgroundColor: "rgba(255,255,255,0.2)",
        },
    },
    lightboxCaption: {
        color: "#e2e8f0",
        textAlign: "center",
        padding: theme.spacing(1.5, 2, 2),
        fontSize: "0.9rem",
    },
}));

const formatAlbumDate = (value) => {
    if (!value) return "—";
    const s = String(value).trim();
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return s;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const resolveSchoolId = (student) => {
    const report = JSON.parse(localStorage.getItem("userDetail")) || {};
    return (
        student?.schooleId?._id ||
        student?.schooleId ||
        report?.schooleId?._id ||
        report?.schooleId ||
        report?.schoolId?._id ||
        report?.schoolId ||
        null
    );
};

export default function ParentMilestonesMemories() {
    const classes = useStyles();
    const [studentList, setStudentList] = useState([]);
    const [selectedStudentId, setSelectedStudentId] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lightbox, setLightbox] = useState({ open: false, index: 0 });

    const allPhotos = useMemo(() => {
        const items = [];
        (albums || []).forEach((album) => {
            (album.imageURL || []).forEach((url) => {
                if (url) {
                    items.push({
                        url,
                        eventName: album.eventName || "Memory",
                        date: album.date,
                    });
                }
            });
        });
        return items;
    }, [albums]);

    const albumPhotoStarts = useMemo(() => {
        let offset = 0;
        return (albums || []).map((album) => {
            const start = offset;
            const count = (album.imageURL || []).filter(Boolean).length;
            offset += count;
            return start;
        });
    }, [albums]);

    useEffect(() => {
        fetchStudentList();
    }, []);

    useEffect(() => {
        if (!selectedStudentId && studentList.length > 0) {
            const first = studentList[0];
            if (first?._id) {
                handleStudentSelect(
                    first._id,
                    first.classId?._id || first.classId || ""
                );
            }
        }
    }, [studentList, selectedStudentId]);

    useEffect(() => {
        if (selectedStudentId) {
            fetchMilestoneImages(selectedStudentId);
        } else {
            setAlbums([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStudentId, studentList]);

    const fetchStudentList = () => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
        const body = { userId: userDetails._id };

        StudentService.findStudentList(body)
            .then((res) => {
                const mapped = (res || []).map((student) => ({
                    ...student,
                    fullName: `${student.firstName || ""} ${student.lastName || ""}`.trim(),
                }));
                setStudentList(mapped);
            })
            .catch((err) => console.error(err));
    };

    const handleStudentSelect = (id, classId) => {
        const student = studentList.find((s) => {
            if (String(s._id) !== String(id)) return false;
            if (!classId) return true;
            const studentClassId = s.classId?._id || s.classId;
            return String(studentClassId) === String(classId);
        });
        setSelectedStudentId(id);
        setSelectedStudent(student || null);
    };

    const fetchMilestoneImages = (studentId) => {
        const student = studentList.find((s) => String(s._id) === String(studentId));
        const schooleId = resolveSchoolId(student);

        setLoading(true);
        StudentService.getStudentMilestoneImages(studentId, { schooleId })
            .then((res) => {
                setAlbums(Array.isArray(res) ? res : []);
            })
            .catch((err) => {
                console.error("Error fetching milestone images", err);
                setAlbums([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const openLightbox = (photoIndex) => {
        setLightbox({ open: true, index: photoIndex });
    };

    const closeLightbox = () => {
        setLightbox({ open: false, index: 0 });
    };

    const showPrev = () => {
        setLightbox((prev) => ({
            ...prev,
            index: prev.index > 0 ? prev.index - 1 : allPhotos.length - 1,
        }));
    };

    const showNext = () => {
        setLightbox((prev) => ({
            ...prev,
            index: prev.index < allPhotos.length - 1 ? prev.index + 1 : 0,
        }));
    };

    const currentPhoto = allPhotos[lightbox.index];

    return (
        <Box className={classes.pageContainer}>
            {studentList.length > 1 && (
                <Card className={classes.studentSelectorCard} elevation={0}>
                    <Box style={{ margin: 8 }}>
                        <StudentCards
                            studentList={studentList}
                            onStudentSelect={handleStudentSelect}
                            selectedStudentId={selectedStudentId}
                        />
                    </Box>
                </Card>
            )}

            <Box className={classes.headerCard}>
                <Box className={classes.headerIcon}>
                    <PhotoLibraryIcon />
                </Box>
                <Box>
                    <Typography className={classes.headerTitle}>
                        MileStones &amp; Memories
                    </Typography>
                    <Typography className={classes.headerSubtitle}>
                        {selectedStudent?.fullName
                            ? `Photo memories for ${selectedStudent.fullName}`
                            : "View captured moments from school events"}
                    </Typography>
                </Box>
            </Box>

            {loading ? (
                <Box className={classes.emptyState} display="flex" justifyContent="center" alignItems="center" gridGap={12}>
                    <CircularProgress size={28} />
                    <Typography variant="body2">Loading memories...</Typography>
                </Box>
            ) : albums.length === 0 ? (
                <Box className={classes.emptyState}>
                    {selectedStudent
                        ? "No photos available for this student yet."
                        : "Select a student to view milestones and memories."}
                </Box>
            ) : (
                albums.map((album, albumIndex) => {
                    const images = (album.imageURL || []).filter(Boolean);
                    const startIndex = albumPhotoStarts[albumIndex] || 0;

                    if (images.length === 0) return null;

                    return (
                        <Box key={album._id} className={classes.albumCard}>
                            <Box className={classes.albumHeader}>
                                <Box>
                                    <Typography className={classes.albumTitle}>
                                        {album.eventName || "Event"}
                                    </Typography>
                                    <Box className={classes.albumMeta} mt={0.5}>
                                        <EventIcon style={{ fontSize: 16 }} />
                                        {formatAlbumDate(album.date)}
                                        {album.academicYear && (
                                            <>
                                                <span>·</span>
                                                <span>{album.academicYear}</span>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                                <span className={classes.photoCount}>
                                    {images.length} photo{images.length !== 1 ? "s" : ""}
                                </span>
                            </Box>
                            <Box className={classes.albumGrid}>
                                <Grid container spacing={1.5}>
                                    {images.map((url, idx) => (
                                        <Grid item xs={6} sm={4} md={3} key={`${album._id}-${idx}`}>
                                            <Box
                                                className={classes.photoTile}
                                                onClick={() => openLightbox(startIndex + idx)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        openLightbox(startIndex + idx);
                                                    }
                                                }}
                                            >
                                                <img
                                                    src={url}
                                                    alt={album.eventName || "Memory"}
                                                    className={classes.photoImage}
                                                    loading="lazy"
                                                />
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Box>
                    );
                })
            )}

            <Dialog
                open={lightbox.open}
                onClose={closeLightbox}
                maxWidth={false}
                PaperProps={{ className: classes.lightboxPaper }}
            >
                <Box className={classes.lightboxContent}>
                    <IconButton className={classes.lightboxClose} onClick={closeLightbox} aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                    {allPhotos.length > 1 && (
                        <>
                            <IconButton
                                className={classes.lightboxNav}
                                style={{ left: 8 }}
                                onClick={showPrev}
                                aria-label="Previous photo"
                            >
                                <ChevronLeftIcon fontSize="large" />
                            </IconButton>
                            <IconButton
                                className={classes.lightboxNav}
                                style={{ right: 8 }}
                                onClick={showNext}
                                aria-label="Next photo"
                            >
                                <ChevronRightIcon fontSize="large" />
                            </IconButton>
                        </>
                    )}
                    {currentPhoto && (
                        <img
                            src={currentPhoto.url}
                            alt={currentPhoto.eventName}
                            className={classes.lightboxImage}
                        />
                    )}
                </Box>
                {currentPhoto && (
                    <Typography className={classes.lightboxCaption}>
                        {currentPhoto.eventName}
                        {currentPhoto.date ? ` · ${formatAlbumDate(currentPhoto.date)}` : ""}
                        {allPhotos.length > 1
                            ? ` · ${lightbox.index + 1} / ${allPhotos.length}`
                            : ""}
                    </Typography>
                )}
            </Dialog>
        </Box>
    );
}
